import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTFieldMaskToSDKFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AddControlApplicationErrors,
  AddControlOptions,
  AddControlResponse,
  DeleteControlApplicationErrors,
  DeleteControlIdentifiers,
  DeleteControlResponse,
  Form,
  FormEventUpdatedEnvelope,
  PublishDraftResponse,
  UpdateControlApplicationErrors,
  UpdateControlIdentifiers,
  UpdateControlOptions,
  UpdateControlResponse,
  UpdateMessagesOptions,
  UpdateMessagesResponse,
  addControl as universalAddControl,
  deleteControl as universalDeleteControl,
  discardDraft as universalDiscardDraft,
  getForm as universalGetForm,
  publishDraft as universalPublishDraft,
  updateControl as universalUpdateControl,
  updateMessages as universalUpdateMessages,
} from './events-v1-form-forms.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/events' };

export function getForm(httpClient: HttpClient): GetFormSignature {
  return (eventId: string) =>
    universalGetForm(
      eventId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetFormSignature {
  /**
   * Retrieves an event registration form.
   * @param - Event ID to which the form belongs.
   * @param - Optional fields.
   * @returns Event form.
   * Form is visible to site visitors.
   */
  (eventId: string): Promise<
    NonNullablePaths<
      Form,
      | `controls`
      | `controls.${number}.type`
      | `controls.${number}.system`
      | `controls.${number}.name`
      | `controls.${number}.label`
      | `controls.${number}.orderIndex`
      | `controls.${number}._id`
      | `messages.rsvp.rsvpYesOption`
      | `messages.rsvp.rsvpNoOption`
      | `messages.rsvp.positiveMessages.title`
      | `messages.rsvp.positiveMessages.confirmation.title`
      | `messages.rsvp.positiveMessages.confirmation.message`
      | `messages.rsvp.positiveMessages.confirmation.addToCalendarActionLabel`
      | `messages.rsvp.positiveMessages.confirmation.shareActionLabel`
      | `messages.rsvp.negativeMessages.title`
      | `messages.rsvp.negativeMessages.confirmation.title`
      | `messages.rsvp.negativeMessages.confirmation.shareActionLabel`
      | `messages.rsvp.submitActionLabel`
      | `messages.checkout.title`
      | `messages.checkout.submitActionLabel`
      | `messages.checkout.confirmation.title`
      | `messages.checkout.confirmation.message`
      | `messages.checkout.confirmation.downloadTicketsLabel`
      | `messages.checkout.confirmation.addToCalendarLabel`
      | `messages.checkout.confirmation.shareEventLabel`
      | `messages.registrationClosed.message`
      | `messages.registrationClosed.exploreEventsActionLabel`
      | `messages.ticketsUnavailable.message`
      | `messages.ticketsUnavailable.exploreEventsActionLabel`,
      6
    >
  >;
}

export function addControl(httpClient: HttpClient): AddControlSignature {
  return (eventId: string, options: AddControlOptions) =>
    universalAddControl(
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface AddControlSignature {
  /**
   * Adds an input control to the form. The applied changes trigger form publishing.
   * @param - Event ID to which the form belongs.
   * @param - Optional fields.
   */
  (eventId: string, options: AddControlOptions): Promise<
    NonNullablePaths<
      AddControlResponse,
      | `_id`
      | `form.controls`
      | `form.controls.${number}.type`
      | `form.controls.${number}.system`
      | `form.controls.${number}.name`
      | `form.controls.${number}.label`
      | `form.controls.${number}.orderIndex`
      | `form.controls.${number}._id`
      | `form.messages.rsvp.rsvpYesOption`
      | `form.messages.rsvp.rsvpNoOption`
      | `form.messages.rsvp.positiveMessages.title`
      | `form.messages.rsvp.positiveMessages.confirmation.title`
      | `form.messages.rsvp.positiveMessages.confirmation.message`
      | `form.messages.rsvp.positiveMessages.confirmation.addToCalendarActionLabel`
      | `form.messages.rsvp.positiveMessages.confirmation.shareActionLabel`
      | `form.messages.rsvp.negativeMessages.title`
      | `form.messages.rsvp.negativeMessages.confirmation.title`
      | `form.messages.rsvp.negativeMessages.confirmation.shareActionLabel`
      | `form.messages.rsvp.submitActionLabel`
      | `form.messages.checkout.title`
      | `form.messages.checkout.submitActionLabel`
      | `form.messages.checkout.confirmation.title`
      | `form.messages.checkout.confirmation.message`
      | `form.messages.checkout.confirmation.downloadTicketsLabel`
      | `form.messages.checkout.confirmation.addToCalendarLabel`
      | `form.messages.checkout.confirmation.shareEventLabel`
      | `form.messages.registrationClosed.message`
      | `form.messages.registrationClosed.exploreEventsActionLabel`
      | `form.messages.ticketsUnavailable.message`
      | `form.messages.ticketsUnavailable.exploreEventsActionLabel`,
      7
    > & {
      __applicationErrorsType?: AddControlApplicationErrors;
    }
  >;
}

export function updateControl(httpClient: HttpClient): UpdateControlSignature {
  return (
    identifiers: NonNullablePaths<UpdateControlIdentifiers, `eventId`, 2>,
    options?: UpdateControlOptions
  ) =>
    universalUpdateControl(
      identifiers,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateControlSignature {
  /**
   * Updates an existing input control in the form. The applied changes trigger form publishing.
   * @param - Optional fields.
   * @param - Identifies what form to update.
   */
  (
    identifiers: NonNullablePaths<UpdateControlIdentifiers, `eventId`, 2>,
    options?: UpdateControlOptions
  ): Promise<
    NonNullablePaths<
      UpdateControlResponse,
      | `form.controls`
      | `form.controls.${number}.type`
      | `form.controls.${number}.system`
      | `form.controls.${number}.name`
      | `form.controls.${number}.label`
      | `form.controls.${number}.orderIndex`
      | `form.controls.${number}._id`
      | `form.messages.rsvp.rsvpYesOption`
      | `form.messages.rsvp.rsvpNoOption`
      | `form.messages.rsvp.positiveMessages.title`
      | `form.messages.rsvp.positiveMessages.confirmation.title`
      | `form.messages.rsvp.positiveMessages.confirmation.message`
      | `form.messages.rsvp.positiveMessages.confirmation.addToCalendarActionLabel`
      | `form.messages.rsvp.positiveMessages.confirmation.shareActionLabel`
      | `form.messages.rsvp.negativeMessages.title`
      | `form.messages.rsvp.negativeMessages.confirmation.title`
      | `form.messages.rsvp.negativeMessages.confirmation.shareActionLabel`
      | `form.messages.rsvp.submitActionLabel`
      | `form.messages.checkout.title`
      | `form.messages.checkout.submitActionLabel`
      | `form.messages.checkout.confirmation.title`
      | `form.messages.checkout.confirmation.message`
      | `form.messages.checkout.confirmation.downloadTicketsLabel`
      | `form.messages.checkout.confirmation.addToCalendarLabel`
      | `form.messages.checkout.confirmation.shareEventLabel`
      | `form.messages.registrationClosed.message`
      | `form.messages.registrationClosed.exploreEventsActionLabel`
      | `form.messages.ticketsUnavailable.message`
      | `form.messages.ticketsUnavailable.exploreEventsActionLabel`,
      7
    > & {
      __applicationErrorsType?: UpdateControlApplicationErrors;
    }
  >;
}

export function deleteControl(httpClient: HttpClient): DeleteControlSignature {
  return (
    identifiers: NonNullablePaths<DeleteControlIdentifiers, `eventId`, 2>
  ) =>
    universalDeleteControl(
      identifiers,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteControlSignature {
  /**
   * Deletes an input control from the form. The applied changes trigger form publishing.
   * @param - Identifies what form to delete.
   */
  (
    identifiers: NonNullablePaths<DeleteControlIdentifiers, `eventId`, 2>
  ): Promise<
    NonNullablePaths<
      DeleteControlResponse,
      | `form.controls`
      | `form.controls.${number}.type`
      | `form.controls.${number}.system`
      | `form.controls.${number}.name`
      | `form.controls.${number}.label`
      | `form.controls.${number}.orderIndex`
      | `form.controls.${number}._id`
      | `form.messages.rsvp.rsvpYesOption`
      | `form.messages.rsvp.rsvpNoOption`
      | `form.messages.rsvp.positiveMessages.title`
      | `form.messages.rsvp.positiveMessages.confirmation.title`
      | `form.messages.rsvp.positiveMessages.confirmation.message`
      | `form.messages.rsvp.positiveMessages.confirmation.addToCalendarActionLabel`
      | `form.messages.rsvp.positiveMessages.confirmation.shareActionLabel`
      | `form.messages.rsvp.negativeMessages.title`
      | `form.messages.rsvp.negativeMessages.confirmation.title`
      | `form.messages.rsvp.negativeMessages.confirmation.shareActionLabel`
      | `form.messages.rsvp.submitActionLabel`
      | `form.messages.checkout.title`
      | `form.messages.checkout.submitActionLabel`
      | `form.messages.checkout.confirmation.title`
      | `form.messages.checkout.confirmation.message`
      | `form.messages.checkout.confirmation.downloadTicketsLabel`
      | `form.messages.checkout.confirmation.addToCalendarLabel`
      | `form.messages.checkout.confirmation.shareEventLabel`
      | `form.messages.registrationClosed.message`
      | `form.messages.registrationClosed.exploreEventsActionLabel`
      | `form.messages.ticketsUnavailable.message`
      | `form.messages.ticketsUnavailable.exploreEventsActionLabel`,
      7
    > & {
      __applicationErrorsType?: DeleteControlApplicationErrors;
    }
  >;
}

export function updateMessages(
  httpClient: HttpClient
): UpdateMessagesSignature {
  return (eventId: string, options?: UpdateMessagesOptions) =>
    universalUpdateMessages(
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateMessagesSignature {
  /**
   * Updates a set of defined form messages that are displayed to a site visitor before, during, and after the registration flow.
   * Includes the configuration of form titles, response labels, "thank you" messages, and call-to-action texts.
   * @param - Event ID to which the form belongs.
   * @param - Optional fields.
   */
  (eventId: string, options?: UpdateMessagesOptions): Promise<
    NonNullablePaths<
      UpdateMessagesResponse,
      | `form.controls`
      | `form.controls.${number}.type`
      | `form.controls.${number}.system`
      | `form.controls.${number}.name`
      | `form.controls.${number}.label`
      | `form.controls.${number}.orderIndex`
      | `form.controls.${number}._id`
      | `form.messages.rsvp.rsvpYesOption`
      | `form.messages.rsvp.rsvpNoOption`
      | `form.messages.rsvp.positiveMessages.title`
      | `form.messages.rsvp.positiveMessages.confirmation.title`
      | `form.messages.rsvp.positiveMessages.confirmation.message`
      | `form.messages.rsvp.positiveMessages.confirmation.addToCalendarActionLabel`
      | `form.messages.rsvp.positiveMessages.confirmation.shareActionLabel`
      | `form.messages.rsvp.negativeMessages.title`
      | `form.messages.rsvp.negativeMessages.confirmation.title`
      | `form.messages.rsvp.negativeMessages.confirmation.shareActionLabel`
      | `form.messages.rsvp.submitActionLabel`
      | `form.messages.checkout.title`
      | `form.messages.checkout.submitActionLabel`
      | `form.messages.checkout.confirmation.title`
      | `form.messages.checkout.confirmation.message`
      | `form.messages.checkout.confirmation.downloadTicketsLabel`
      | `form.messages.checkout.confirmation.addToCalendarLabel`
      | `form.messages.checkout.confirmation.shareEventLabel`
      | `form.messages.registrationClosed.message`
      | `form.messages.registrationClosed.exploreEventsActionLabel`
      | `form.messages.ticketsUnavailable.message`
      | `form.messages.ticketsUnavailable.exploreEventsActionLabel`,
      7
    >
  >;
}

export function publishDraft(httpClient: HttpClient): PublishDraftSignature {
  return (eventId: string) =>
    universalPublishDraft(
      eventId,
      // @ts-ignore
      { httpClient }
    );
}

interface PublishDraftSignature {
  /**
   * Publishes the draft form.
   * @param - Event ID to which the form belongs.
   * @deprecated
   */
  (eventId: string): Promise<
    NonNullablePaths<
      PublishDraftResponse,
      | `form.controls`
      | `form.controls.${number}.type`
      | `form.controls.${number}.system`
      | `form.controls.${number}.name`
      | `form.controls.${number}.label`
      | `form.controls.${number}.orderIndex`
      | `form.controls.${number}._id`
      | `form.messages.rsvp.rsvpYesOption`
      | `form.messages.rsvp.rsvpNoOption`
      | `form.messages.rsvp.positiveMessages.title`
      | `form.messages.rsvp.positiveMessages.confirmation.title`
      | `form.messages.rsvp.positiveMessages.confirmation.message`
      | `form.messages.rsvp.positiveMessages.confirmation.addToCalendarActionLabel`
      | `form.messages.rsvp.positiveMessages.confirmation.shareActionLabel`
      | `form.messages.rsvp.negativeMessages.title`
      | `form.messages.rsvp.negativeMessages.confirmation.title`
      | `form.messages.rsvp.negativeMessages.confirmation.shareActionLabel`
      | `form.messages.rsvp.submitActionLabel`
      | `form.messages.checkout.title`
      | `form.messages.checkout.submitActionLabel`
      | `form.messages.checkout.confirmation.title`
      | `form.messages.checkout.confirmation.message`
      | `form.messages.checkout.confirmation.downloadTicketsLabel`
      | `form.messages.checkout.confirmation.addToCalendarLabel`
      | `form.messages.checkout.confirmation.shareEventLabel`
      | `form.messages.registrationClosed.message`
      | `form.messages.registrationClosed.exploreEventsActionLabel`
      | `form.messages.ticketsUnavailable.message`
      | `form.messages.ticketsUnavailable.exploreEventsActionLabel`,
      7
    >
  >;
}

export function discardDraft(httpClient: HttpClient): DiscardDraftSignature {
  return (eventId: string) =>
    universalDiscardDraft(
      eventId,
      // @ts-ignore
      { httpClient }
    );
}

interface DiscardDraftSignature {
  /**
   * Clears all changes to the draft form.
   * @param - Event ID to which the form belongs.
   * @deprecated
   */
  (eventId: string): Promise<void>;
}

export const onFormEventUpdated = EventDefinition(
  'wix.events.events.EventUpdated',
  false,
  (event: FormEventUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.timestamp' },
            { path: 'data.scheduleConfig.startDate' },
            { path: 'data.scheduleConfig.endDate' },
            { path: 'data.scheduleConfig.recurrences.occurrences.startDate' },
            { path: 'data.scheduleConfig.recurrences.occurrences.endDate' },
            { path: 'data.event.created' },
            { path: 'data.event.modified' },
            { path: 'data.event.publishedDate' },
            { path: 'data.event.scheduling.config.startDate' },
            { path: 'data.event.scheduling.config.endDate' },
            {
              path: 'data.event.scheduling.config.recurrences.occurrences.startDate',
            },
            {
              path: 'data.event.scheduling.config.recurrences.occurrences.endDate',
            },
            { path: 'data.event.registration.rsvpCollection.config.startDate' },
            { path: 'data.event.registration.rsvpCollection.config.endDate' },
            { path: 'data.event.categories.createdDate' },
            { path: 'data.event.longDescription.metadata.createdTimestamp' },
            { path: 'data.event.longDescription.metadata.updatedTimestamp' },
            { path: 'data.event.badges.functionUpdateDate' },
          ],
        },
        {
          transformFn: transformRESTFieldMaskToSDKFieldMask,
          paths: [{ path: 'data.fields' }],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'data.location.fullAddress' },
            { path: 'data.event.location.fullAddress' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'data.location.coordinates.lat' },
            { path: 'data.location.coordinates.lng' },
            { path: 'data.event.location.coordinates.lat' },
            { path: 'data.event.location.coordinates.lng' },
            {
              path: 'data.event.longDescription.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'data.event.longDescription.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'data.event.longDescription.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'data.event.longDescription.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'data.event.longDescription.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'data.event.longDescription.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'data.event.longDescription.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'data.event.longDescription.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'data.event.longDescription.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'data.event.longDescription.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'data.event.longDescription.nodes.imageData.image.duration',
            },
            {
              path: 'data.event.longDescription.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'data.event.longDescription.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'data.event.longDescription.nodes.pollData.poll.image.duration',
            },
            {
              path: 'data.event.longDescription.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'data.event.longDescription.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'data.event.longDescription.nodes.appEmbedData.image.duration',
            },
            {
              path: 'data.event.longDescription.nodes.videoData.video.duration',
            },
            {
              path: 'data.event.longDescription.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'data.event.longDescription.nodes.audioData.audio.duration',
            },
            {
              path: 'data.event.longDescription.nodes.audioData.coverImage.duration',
            },
            {
              path: 'data.event.longDescription.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'data.event.longDescription.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'data.event.longDescription.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'data.event.longDescription.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'data.event.longDescription.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'data.event.longDescription.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'data.event.longDescription.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'data.event.longDescription.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'data.event.longDescription.nodes.shapeData.shape.duration',
            },
            {
              path: 'data.event.longDescription.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'data.event.longDescription.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'data.event.longDescription.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'data.event.longDescription.nodes.cardData.backgroundImage.media.duration',
            },
            { path: 'data.event.longDescription.nodes.tocData.fontSize' },
            { path: 'data.event.longDescription.nodes.tocData.itemSpacing' },
            {
              path: 'data.event.longDescription.nodes.smartBlockCellData.shape.duration',
            },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'data.event.mainImage' }],
        },
      ])
    )
)<FormEventUpdatedEnvelope>();

export {
  AccountInfo,
  ActionEvent,
  AddControlOptions,
  AddControlOptionsControlOneOf,
  AddControlRequest,
  AddControlRequestControlOneOf,
  AddControlResponse,
  AdditionalGuestsControl,
  Address,
  AddressControl,
  AddressControlLabels,
  AddressLocation,
  AddressStreetOneOf,
  Agenda,
  Alignment,
  AnchorData,
  AppEmbedData,
  AppEmbedDataAppDataOneOf,
  AppType,
  AspectRatio,
  AudioData,
  Backdrop,
  BackdropType,
  Background,
  BackgroundGradient,
  BackgroundImage,
  BackgroundType,
  Badge,
  Banner,
  BaseEventMetadata,
  BlockquoteData,
  BookingData,
  Border,
  BorderColors,
  BorderWidths,
  BulletedListData,
  ButtonData,
  ButtonDataType,
  ButtonStyles,
  CalendarLinks,
  CaptionData,
  CardData,
  CardDataBackground,
  CardDataBackgroundType,
  CardStyles,
  CardStylesType,
  Category,
  CategoryCounts,
  CellStyle,
  CheckboxControl,
  CheckoutFormMessages,
  CheckoutType,
  CodeBlockData,
  CollapsibleListData,
  ColorData,
  Colors,
  ColumnSize,
  ConferenceType,
  Crop,
  Dashboard,
  DateControl,
  Decoration,
  DecorationDataOneOf,
  DecorationType,
  DeleteControlIdentifiers,
  DeleteControlRequest,
  DeleteControlResponse,
  Design,
  DesignTarget,
  Dimensions,
  Direction,
  DiscardDraftRequest,
  DiscardDraftResponse,
  DividerData,
  DividerDataAlignment,
  DocumentStyle,
  DomainEvent,
  DomainEventBodyOneOf,
  DropdownControl,
  EmailControl,
  EmbedData,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  Event,
  EventData,
  EventDisplaySettings,
  EventStatus,
  EventType,
  EventUpdated,
  ExternalEvent,
  Feed,
  FileData,
  FileSource,
  FileSourceDataOneOf,
  FontFamilyData,
  FontSizeData,
  FontType,
  Form,
  FormEventUpdatedEnvelope,
  FormInputControlAdded,
  FormInputControlDeleted,
  FormInputControlUpdated,
  FormMessages,
  GIF,
  GIFData,
  GIFType,
  GalleryData,
  GalleryOptions,
  GalleryOptionsLayout,
  GetFormRequest,
  GetFormResponse,
  Gradient,
  GradientType,
  GuestListConfig,
  HTMLData,
  HTMLDataDataOneOf,
  HeadingData,
  Height,
  IdentificationData,
  IdentificationDataIdOneOf,
  Image,
  ImageData,
  ImageDataStyles,
  ImagePosition,
  ImagePositionPosition,
  ImageScalingScaling,
  ImageStyles,
  Indentation,
  InitialExpandedItems,
  Input,
  InputControl,
  InputControlType,
  Item,
  ItemDataOneOf,
  ItemStyle,
  Keyword,
  Label,
  LabellingSettings,
  Labels,
  Layout,
  LayoutCellData,
  LayoutData,
  LayoutDataBackground,
  LayoutDataBackgroundImage,
  LayoutDataBackgroundType,
  LayoutType,
  LineStyle,
  Link,
  LinkData,
  LinkDataOneOf,
  LinkPreviewData,
  LinkPreviewDataStyles,
  ListStyle,
  ListValue,
  Location,
  LocationType,
  MapCoordinates,
  MapData,
  MapSettings,
  MapType,
  Media,
  MentionData,
  MessageEnvelope,
  Metadata,
  Money,
  NameControl,
  NameControlLabels,
  Negative,
  NegativeResponseConfirmation,
  Node,
  NodeDataOneOf,
  NodeStyle,
  NodeType,
  NullValue,
  Occurrence,
  Oembed,
  OnlineConferencing,
  OnlineConferencingConfig,
  OnlineConferencingSession,
  Option,
  OptionDesign,
  OptionLayout,
  OptionSelection,
  OptionSelectionSelectedOptionOneOf,
  OrderedListData,
  Orientation,
  Origin,
  PDFSettings,
  ParagraphData,
  Permissions,
  PhoneControl,
  Placement,
  PlaybackOptions,
  PluginContainerData,
  PluginContainerDataAlignment,
  PluginContainerDataWidth,
  PluginContainerDataWidthDataOneOf,
  Poll,
  PollData,
  PollDataLayout,
  PollDesign,
  PollDesignBackground,
  PollDesignBackgroundBackgroundOneOf,
  PollDesignBackgroundType,
  PollLayout,
  PollLayoutDirection,
  PollLayoutType,
  PollSettings,
  Position,
  Positive,
  PositiveResponseConfirmation,
  PricingData,
  PublishDraftRequest,
  PublishDraftResponse,
  RadioButtonControl,
  Recurrences,
  Registration,
  RegistrationClosedMessages,
  RegistrationStatus,
  Rel,
  RequestedFields,
  Resizing,
  ResponseConfirmation,
  ResponsivenessBehaviour,
  RestoreInfo,
  RibbonStyles,
  RichContent,
  RsvpCollection,
  RsvpCollectionConfig,
  RsvpConfirmationMessages,
  RsvpConfirmationMessagesNegativeResponseConfirmation,
  RsvpConfirmationMessagesPositiveResponseConfirmation,
  RsvpFormMessages,
  RsvpStatusOptions,
  RsvpSummary,
  Scaling,
  ScheduleConfig,
  Scheduling,
  SeoSchema,
  SeoSettings,
  Settings,
  ShapeData,
  ShapeDataStyles,
  SiteUrl,
  SmartBlockCellData,
  SmartBlockData,
  SmartBlockDataType,
  Source,
  Spoiler,
  SpoilerData,
  State,
  Status,
  Stop,
  StreetAddress,
  Styles,
  StylesBorder,
  StylesPosition,
  Subdivision,
  SubdivisionType,
  TableCellData,
  TableData,
  Tag,
  Target,
  TaxConfig,
  TaxType,
  TextAlignment,
  TextControl,
  TextData,
  TextNodeStyle,
  TextStyle,
  Thumbnails,
  ThumbnailsAlignment,
  Ticketing,
  TicketingConfig,
  TicketingSummary,
  TicketsConfirmationMessages,
  TicketsUnavailableMessages,
  TocData,
  Type,
  UpdateControlIdentifiers,
  UpdateControlIdentifiersControlOneOf,
  UpdateControlOptions,
  UpdateControlOptionsControlOneOf,
  UpdateControlRequest,
  UpdateControlRequestControlOneOf,
  UpdateControlResponse,
  UpdateMessagesOptions,
  UpdateMessagesRequest,
  UpdateMessagesResponse,
  ValueType,
  VerticalAlignment,
  VerticalAlignmentAlignment,
  Video,
  VideoData,
  ViewMode,
  ViewRole,
  VisitorType,
  VoteRole,
  WebhookIdentityType,
  Width,
  WidthType,
} from './events-v1-form-forms.universal.js';
